import { CHART_DEFAULTS, GenericChartOptions, GenericChartProps, FILLED_AND_UNFILLED_COLOURS } from './constants';
import { areAllVariablesCurrencyType, CurrencyConfig, getCurrencyAxisFormat } from '@routes/custom-dashboard/items/charts/multi-utrs-chart/utils';
import { BaseHistoricalUtr } from '@routes/custom-dashboard/items/types';

export const chartStyleHorizontalBar: GenericChartOptions = {
    bar: {
        groupWidth: '100%',
        width: '100%'
    },
    legend: {
        position: 'none'
    },
    chartArea: {
        top: 20,
        bottom: 40,
        left: 0,
        right: 0,
    },
    hAxis: {
        gridlines: {
            color: 'transparent'
        },
        textPosition: 'none',
        minValue: 0,
    },
}

export const chartStylePieLegend: GenericChartOptions = {
    legend: {
        position: 'left'
    },
    chartArea: {
        top: 10,
        bottom: 10,
        left: 20,
        right: 10,
    },
}

export const chartStyleFullPie: GenericChartOptions = {
  pieHole: 0,
};

export const chartStyleStacked: GenericChartOptions = {
    isStacked: true
}

export const chartStyleNames: { [key: string]: string } = {
  chartStyleHorizontalBar: 'chartStyleHorizontalBar',
  chartStylePieLegend: 'chartStylePieLegend',
  chartStyleStacked: 'chartStyleStacked',
  chartStyleFullPie: 'chartStyleFullPie',
};

export const chartStyleMap: { [key: string]: GenericChartOptions } = {
  chartStyleHorizontalBar,
  chartStylePieLegend,
  chartStyleStacked,
  chartStyleFullPie,
};

interface AxisOptions {
  vAxisTitle?: string;
  vAxisMaxValue?: number;
  currencyAxisFormat?: string;
}

const getOptionsByType = (
  chartType: GenericChartProps['chartType'],
  { vAxisTitle, vAxisMaxValue, currencyAxisFormat }: AxisOptions
) => {
  // For most charts, values are on vAxis. For BarChart, values are on hAxis.
  const vAxisFormat = chartType !== 'BarChart' ? currencyAxisFormat : undefined;
  const hAxisFormat = chartType === 'BarChart' ? currencyAxisFormat : undefined;

  const vAxisOptions = {
    title: vAxisTitle,
    format: vAxisFormat ?? 'short',
  };

  switch (chartType) {
    case 'PieChart':
      return {
        pieHole: 0.7,
        pieSliceText: 'label',
        legend: 'none',
        chartArea: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10,
        },
        vAxis: vAxisOptions
      };
    case 'LineChart':
      // Vertical axis of line chart should have a minimum value of 0 to deal with big number
      return {
        vAxis: {
          ...vAxisOptions,
          minValue: 0,
          maxValue: vAxisMaxValue,
        },
      };
    case 'ColumnChart':
      // Vertical axis of column chart should have a minimum value of 0 to deal with big number
      // Eg: The issue happens when all data have same value with a big number
      return {
        vAxis: {
          ...vAxisOptions,
          minValue: 0
        }
      }
    case 'BarChart':
      // For horizontal bar charts, value axis is hAxis; categories are on vAxis
      return {
        vAxis: { format: 'short' },
        hAxis: {
          title: vAxisTitle,
          ...(hAxisFormat ? { format: hAxisFormat } : {}),
        },
      };
    default:
      return {
        vAxis: vAxisOptions
      };
  }
};

/**
 * Shared chart props for formatting/display options.
 * Add new shared props here - they will be automatically available to all charts.
 *
 * When adding new props:
 * 1. Add to this interface
 * 2. Add to BaseChartComponentProps in types.ts
 * 3. Update toChartProps() below to extract the new prop
 */
export interface ChartSharedProps {
  currencyConfig?: CurrencyConfig;
  // Add future shared props here (e.g., locale, numberFormat, etc.)
}

interface ChartPropsInput extends Partial<ChartSharedProps> {
  utrsData?: BaseHistoricalUtr[];
  variables?: Record<string, { code: string; valueListCode?: string }>;
}

/**
 * Extracts ChartSharedProps from component props.
 * Use this in chart components: chartProps: toChartProps(props)
 *
 * This centralizes extraction so adding new shared props only requires
 * updating this function - not every chart component.
 *
 * Only includes currencyConfig when ALL variables are currency type.
 * This is because axis formatting is chart-wide - mixed currency/non-currency
 * metrics shouldn't show currency on the axis.
 */
export const toChartProps = <T extends ChartPropsInput>(props: T): ChartSharedProps => {
  const { currencyConfig, utrsData, variables } = props;

  // Only include currency config if ALL variables have unitType: 'currency'
  // (axis format is chart-wide, so mixed metrics shouldn't show currency)
  const allVariablesAreCurrency = utrsData ? areAllVariablesCurrencyType(variables, utrsData) : false;

  return {
    currencyConfig: allVariablesAreCurrency ? currencyConfig : undefined,
    // Add future shared props here
  };
};

type ChartOptionMerge = {
  chartType: GenericChartProps['chartType'];
  options?: GenericChartProps['options'];
  vAxisTitle?: string;
  vAxisMaxValue?: number;
  /** Pass shared props from chart component */
  chartProps?: ChartSharedProps;
  hasUnfilledPortion?: boolean;
};

export const getChartOptions = ({ chartType, options, vAxisTitle, vAxisMaxValue, chartProps, hasUnfilledPortion }: ChartOptionMerge) => {
  const chartOptions = options ? chartStyleMap[options] : undefined;
  const currencyAxisFormat = getCurrencyAxisFormat(chartProps?.currencyConfig?.currencySymbol);
  const baseOptions = getOptionsByType(chartType, { vAxisTitle, vAxisMaxValue, currencyAxisFormat });

  // Deep merge for hAxis to preserve both base and chart-specific settings
  const baseHAxis = 'hAxis' in baseOptions ? baseOptions.hAxis : undefined;
  const chartHAxis = chartOptions?.hAxis;
  const mergedHAxis = baseHAxis || chartHAxis ? { ...baseHAxis, ...chartHAxis } : undefined;

  return {
    ...CHART_DEFAULTS.OPTIONS,
    ...baseOptions,
    ...chartOptions,
    ...(mergedHAxis ? { hAxis: mergedHAxis } : {}),
    ...(hasUnfilledPortion ? { colors: FILLED_AND_UNFILLED_COLOURS, pieSliceText: 'none' } : {}),
  };
};
